import React, { useEffect, useState } from "react";
import { Copy, Users, Trophy, Sparkles, DollarSign, TrendingUp } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "../../lib/api";

export default function ReferralsPage() {
  const [data, setData] = useState(null);
  const [redeeming, setRedeeming] = useState(false);

  async function load() {
    const { data } = await api.get("/referrals/me");
    setData(data);
  }
  useEffect(() => { load().catch(() => {}); }, []);

  if (!data) return null;
  const link = `${window.location.origin}${data.referral_link}`;

  function copy() {
    navigator.clipboard.writeText(link);
    toast.success("Referral link copied");
  }

  async function redeem() {
    setRedeeming(true);
    try {
      const { data: r } = await api.post("/referrals/redeem", { points: data.points_for_free_month });
      toast.success(`${r.months_credited} free month credited!`);
      load();
    } catch (e) { toast.error(formatApiError(e)); } finally { setRedeeming(false); }
  }

  const canRedeem = data.points_balance >= data.points_for_free_month;

  return (
    <div data-testid="referrals-page">
      <p className="font-script text-[var(--gold)] text-3xl">Affiliate Program</p>
      <h1 className="font-serif font-light text-4xl mb-3">Earn cash & free months</h1>
      <p className="text-[var(--muted)] mb-8 max-w-xl">Refer couples, planners, or vendors. Earn 30% recurring on every direct signup, plus 10% level-2 and 5% level-3 commissions in our multi-level affiliate program. Plus points for free subscription months.</p>

      <div className="grid md:grid-cols-3 gap-4 mb-8">
        <div className="bg-white border border-[var(--line)] rounded-2xl p-5">
          <Sparkles className="w-5 h-5 text-[var(--gold)] mb-2" />
          <p className="font-serif text-3xl">{data.points_balance}</p>
          <p className="text-[11px] uppercase tracking-[0.16em] text-[var(--muted)] mt-1">Points balance</p>
        </div>
        <div className="bg-[var(--bg-warm)] rounded-2xl p-5">
          <DollarSign className="w-5 h-5 text-[var(--gold)] mb-2" />
          <p className="font-serif text-3xl">${data.lifetime_cash_usd.toFixed(2)}</p>
          <p className="text-[11px] uppercase tracking-[0.16em] text-[var(--muted)] mt-1">Lifetime cash earned</p>
        </div>
        <div className="bg-[var(--ink)] text-white rounded-2xl p-5">
          <Users className="w-5 h-5 text-[var(--gold)] mb-2" />
          <p className="font-serif text-3xl text-[var(--gold)]">{data.downline.level_1_count}/{data.downline.level_2_count}/{data.downline.level_3_count}</p>
          <p className="text-[11px] uppercase tracking-[0.16em] text-white/60 mt-1">Levels 1 / 2 / 3</p>
        </div>
      </div>

      <div className="bg-white border border-[var(--line)] rounded-2xl p-6 md:p-8 mb-8">
        <p className="text-[11px] uppercase tracking-[0.18em] text-[var(--gold)] mb-3">Your referral link</p>
        <div className="flex flex-col md:flex-row gap-3 items-start md:items-center">
          <div className="flex-1 bg-[var(--bg-warm)] rounded-full px-5 py-3 font-mono text-sm overflow-x-auto whitespace-nowrap" data-testid="referral-link">{link}</div>
          <button onClick={copy} data-testid="copy-referral" className="btn-gold"><Copy className="w-4 h-4" /> Copy</button>
        </div>
        <p className="text-xs text-[var(--muted)] mt-3">Code: <span className="font-mono font-semibold text-[var(--ink)]">{data.referral_code}</span></p>
      </div>

      <div className="grid lg:grid-cols-2 gap-6 mb-8">
        <div className="bg-white border border-[var(--line)] rounded-2xl p-6 md:p-8">
          <p className="text-[11px] uppercase tracking-[0.18em] text-[var(--gold)] mb-4">Commission Tiers</p>
          <div className="space-y-3">
            {data.config.map((tier) => (
              <div key={tier.level} className="flex items-center gap-4 py-2 border-b border-[var(--line)] last:border-0">
                <div className="w-8 h-8 rounded-full bg-[var(--gold)] text-white flex items-center justify-center font-serif">{tier.level}</div>
                <div className="flex-1">
                  <p className="font-medium text-sm">Level {tier.level}</p>
                  <p className="text-xs text-[var(--muted)]">{tier.commission_pct}% cash commission · {tier.points} pts</p>
                </div>
                <TrendingUp className="w-4 h-4 text-[var(--gold)]" />
              </div>
            ))}
          </div>
        </div>

        <div className="bg-[var(--bg-warm)] rounded-2xl p-6 md:p-8">
          <p className="text-[11px] uppercase tracking-[0.18em] text-[var(--gold)] mb-2">Redeem Points</p>
          <p className="font-serif text-2xl mb-1">{data.points_for_free_month} pts = 1 free month</p>
          <p className="text-sm text-[var(--muted)] mb-6">Auto-upgrades you to Premium and credits the free time.</p>
          <button onClick={redeem} disabled={!canRedeem || redeeming} data-testid="redeem-points" className="btn-gold w-full disabled:opacity-50">
            {redeeming ? "Redeeming…" : canRedeem ? `Redeem ${data.points_for_free_month} pts` : `Need ${data.points_for_free_month - data.points_balance} more pts`}
          </button>
        </div>
      </div>

      <div className="bg-white border border-[var(--line)] rounded-2xl p-6 md:p-8 mb-8">
        <p className="text-[11px] uppercase tracking-[0.18em] text-[var(--gold)] mb-4">Your direct referrals</p>
        {data.downline.level_1.length === 0 && <p className="text-sm text-[var(--muted)] py-6 text-center">No referrals yet. Share your link to get started.</p>}
        {data.downline.level_1.map((u) => (
          <div key={u.user_id} className="flex items-center gap-3 py-3 border-b border-[var(--line)] last:border-0">
            <div className="w-9 h-9 rounded-full bg-[var(--bg-warm)] flex items-center justify-center font-serif">{(u.name || u.email)[0]?.toUpperCase()}</div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium truncate">{u.name || u.email}</p>
              <p className="text-xs text-[var(--muted)]">Joined {new Date(u.created_at).toLocaleDateString()}</p>
            </div>
            <span className={`text-[10px] uppercase tracking-wider px-2 py-1 rounded-full ${u.plan === "free" ? "bg-[var(--bg-warm)] text-[var(--muted)]" : "bg-[var(--gold)] text-white"}`}>{u.plan}</span>
          </div>
        ))}
      </div>

      {data.transactions?.length > 0 && (
        <div className="bg-white border border-[var(--line)] rounded-2xl p-6 md:p-8">
          <p className="text-[11px] uppercase tracking-[0.18em] text-[var(--gold)] mb-4">Recent transactions</p>
          <div className="space-y-2">
            {data.transactions.slice(0, 10).map((t) => (
              <div key={t.txn_id} className="flex items-center justify-between py-2 border-b border-[var(--line)] last:border-0">
                <div>
                  <p className="text-sm font-medium">{t.kind === "redemption" ? "Points redemption" : `L${t.level} commission`}{t.plan_id && ` · ${t.plan_id}`}</p>
                  <p className="text-xs text-[var(--muted)]">{new Date(t.created_at).toLocaleString()}{t.note ? ` · ${t.note}` : ""}</p>
                </div>
                <div className="text-right">
                  {t.cash_awarded > 0 && <p className="text-sm font-medium text-[var(--gold)]">+${t.cash_awarded.toFixed(2)}</p>}
                  <p className="text-xs text-[var(--muted)]">{t.points_awarded > 0 ? `+${t.points_awarded}` : t.points_awarded} pts</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
